import React, { useState } from 'react';
import { Phone, ArrowRight, Flame, Star } from 'lucide-react';
import SectionTitle from '../components/ui/SectionTitle';
import { menuCategories, images, foodTruckInfo } from '../data/mock';

const MenuPage = () => {
  const [activeCategory, setActiveCategory] = useState('Tacos');

  const categoryImages = {
    'Tacos': images.tacos1,
    'Burgers & Américains': images.burger1,
    'Hot-dogs': images.burger2,
    'Crêpes': images.burger3,
    'Frites & Sides': images.tacos2,
    'Boissons': images.tacos3
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0E0E0E]">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.burger1}
            alt="Menu"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0E] via-[#0E0E0E]/95 to-[#0E0E0E]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7C948]/10 border border-[#F7C948]/30 text-[#F7C948] text-sm font-medium mb-6">
              <Flame size={16} />
              Notre Carte
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white mb-4">
              Le Menu
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Burgers, tacos, crêpes et bien plus encore. Tous nos plats sont préparés sur commande avec des produits frais.
            </p>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="sticky top-20 z-40 bg-[#0E0E0E]/95 backdrop-blur-md border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 gap-2 scrollbar-hide">
            {menuCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => setActiveCategory(category.name)}
                className={`flex-shrink-0 px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                  activeCategory === category.name
                    ? 'bg-[#F7C948] text-[#0E0E0E]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Menu Items */}
            <div className="lg:col-span-2">
              {menuCategories.map((category) => (
                <div
                  key={category.name}
                  id={category.name.toLowerCase().replace(/\s/g, '-')}
                  className={`mb-12 ${activeCategory === category.name ? '' : 'hidden lg:block'}`}
                >
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#F7C948]/10 rounded-xl flex items-center justify-center">
                      <span className="text-[#F7C948]">●</span>
                    </span>
                    {category.name}
                  </h2>

                  <div className="space-y-4">
                    {category.items.map((item, index) => (
                      <div
                        key={index}
                        className={`group bg-[#1A1A1A] rounded-2xl p-5 border transition-all duration-300 ${
                          item.featured
                            ? 'border-[#F7C948]/30 hover:border-[#F7C948]'
                            : 'border-white/5 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-display text-lg font-bold text-white group-hover:text-[#F7C948] transition-colors duration-300">
                                {item.name}
                              </h3>
                              {item.featured && (
                                <span className="px-2 py-0.5 bg-[#F7C948] text-[#0E0E0E] text-xs font-bold rounded-md">
                                  POPULAIRE
                                </span>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">{item.description}</p>
                          </div>
                          <span className="text-[#F7C948] font-bold text-lg flex-shrink-0">
                            {item.price}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-40">
                {/* Featured Image */}
                <div className="relative rounded-2xl overflow-hidden mb-6 h-64">
                  <img
                    src={categoryImages[activeCategory] || images.burger1}
                    alt={activeCategory}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="text-[#F7C948] font-display text-lg font-bold">
                      {activeCategory}
                    </span>
                  </div>
                </div>

                {/* Order CTA */}
                <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-[#F7C948]/20">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-[#F7C948] fill-[#F7C948]" />
                    ))}
                    <span className="text-white font-medium ml-2">5/5</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">
                    Envie de commander ?
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Appelez-nous pour passer commande ou passez directement au food truck !
                  </p>
                  <a
                    href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                    className="w-full flex items-center justify-center gap-2 bg-[#F7C948] text-[#0E0E0E] px-6 py-3 rounded-xl font-semibold hover:bg-[#FFE17D] transition-all duration-300"
                  >
                    <Phone size={18} />
                    {foodTruckInfo.phone}
                  </a>
                </div>

                {/* Info */}
                <div className="mt-6 bg-[#0E0E0E] rounded-2xl p-5 border border-white/10">
                  <p className="text-gray-400 text-sm">
                    <span className="text-[#F7C948]">●</span> Tous nos plats sont préparés à la commande
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    <span className="text-[#F7C948]">●</span> Ingrédients frais et de qualité
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    <span className="text-[#F7C948]">●</span> Portions généreuses garanties
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Une question sur notre carte ?
          </h2>
          <p className="text-gray-400 mb-8">
            N'hésitez pas à nous contacter pour plus d'informations
          </p>
          <a
            href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Phone size={20} />
            Nous appeler
          </a>
        </div>
      </section>
    </main>
  );
};

export default MenuPage;
